import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { ShotListRowDict, ShotProductionPlanDictYMap, ShotProductionStateDictYMap } from '../../../../generated/nodeDataTypes'

function required<T>(value: T | undefined, field: string): T {
  if (value === undefined) throw new Error(`Production details are missing ${field}`)
  return value
}

function timecode(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1000)
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}.${Math.floor(milliseconds % 1000 / 100)}`
}

function stateLabel(values: readonly ShotProductionStateDictYMap[]): string {
  return values.map(value => `${required(value.element_id, 'element')} = ${required(value.value, 'value')} (${required(value.truth, 'truth')})`).join('; ') || 'None'
}

type ProductionDetailsProps = {
  plan: ShotProductionPlanDictYMap
  rows: readonly ShotListRowDict[]
  currentGenerationId: string | undefined
  currentScreenplayFingerprint: string | undefined
}

/** Ten-field §6 card is a read-only projection of the validated server record. */
export function ProductionDetails({ plan, rows, currentGenerationId, currentScreenplayFingerprint }: ProductionDetailsProps) {
  const { t } = useTranslation('canvas')
  if (plan.version !== 1) throw new Error('Unsupported Shot List production-plan version')
  const stale = plan.baseline_generation_id !== currentGenerationId || plan.screenplay_fingerprint !== currentScreenplayFingerprint
  if (stale) return <div data-testid="shot-production-details" className="bg-surface-0 p-5 text-sm text-foreground">
    <p role="alert" className="rounded-lg border border-warning/50 bg-surface-1 p-4 text-warning">{t('shotList.productionStale')}</p>
  </div>
  const shots = required(plan.shots, 'shots')
  const seams = required(plan.seams, 'seams')
  const blockers = required(plan.render_blockers, 'render blockers')
  const decisions = required(plan.pending_decisions, 'pending decisions')
  if (shots.length !== rows.length || seams.length !== rows.length + 1) throw new Error('Production details no longer match the Shot List')
  let cursor = 0
  const times = rows.map(row => {
    const start = cursor
    cursor += row.duration_ms
    return `${timecode(start)}–${timecode(cursor)} (${(row.duration_ms / 1000).toFixed(1)} s)`
  })
  return <div data-testid="shot-production-details" className="space-y-6 overflow-auto bg-surface-0 p-5 text-sm text-foreground">
    <div className="rounded-xl border border-border bg-surface-1 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-medium">{t('shotList.productionTitle')}</h3>
        <span className="rounded-full border border-warning/50 px-2 py-0.5 text-xs text-warning">{t('shotList.productionRender', { readiness: required(plan.render_readiness, 'render readiness') })}</span>
      </div>
      <p className="mt-1 text-muted-foreground">{t('shotList.productionSummary', { status: required(plan.planning_status, 'planning status'), count: shots.length })}</p>
      <p className="mt-2 text-muted-foreground">{t('shotList.productionBlocked', { blockers: blockers.join(', ') || 'None' })}</p>
    </div>
    <section aria-label="Running order" className="rounded-lg border border-border bg-surface-1 p-4">
      <h3 className="mb-2 font-medium">{t('shotList.productionRunningOrder')}</h3>
      <ol className="space-y-1 text-muted-foreground">
        {shots.map((shot, index) => <li key={required(shot.shot_id, 'shot ID')}>
          {required(shot.shot_id, 'shot ID')} · {times[index]} · {required(shot.flow, 'narrative flow')}
        </li>)}
      </ol>
    </section>
    <section aria-label="Shot cards" className="space-y-3">
      <h3 className="font-medium">{t('shotList.productionCards')}</h3>
      {shots.map((shot, index) => {
        const row = rows[index]
        if (shot.shot_id !== row.id) throw new Error(`Production shot ${index + 1} no longer matches the Shot List`)
        const source = required(shot.source, 'source map')
        const speech = source.filter(item => ['dialogue', 'os', 'voiceover'].includes(required(item.entry_type, 'entry type')))
          .map(item => `${item.speaker || 'Unknown'}: “${required(item.text, 'dialogue text')}”`)
        const previous = index ? shots[index - 1] : undefined
        const outgoing = seams[index + 1]
        const changes = required(shot.changes, 'owned changes').map(change => `${required(change.element_id, 'element')} (${required(change.owner, 'owner')}: ${required(change.account, 'account') || 'on screen'})`)
        const fields: [string, ReactNode][] = [
          [t('shotList.productionField1'), `SC${required(shot.scene_ordinal, 'scene')} · ${row.id} · ${required(shot.strand_id, 'strand')}`],
          [t('shotList.productionField2'), times[index]],
          [t('shotList.productionField3'), <span key="motion" className="whitespace-pre-wrap">{t('shotList.productionMotionLines', { flow: required(shot.flow, 'flow'), action: required(shot.action, 'action'), prompt: shot.motion_prompt || t('shotList.productionPendingPass') })}</span>],
          [t('shotList.productionField4'), `${row.location || t('shotList.unspecified')} · ${t('shotList.productionLightPending')} · ${t('shotList.productionLockPending')}`],
          [t('shotList.productionField5'), `${row.framing_shot || t('shotList.unspecified')} · ${row.framing_angle || t('shotList.unspecified')} · ${t('shotList.productionPositionPending')}`],
          [t('shotList.productionField6'), `${row.framing_lens || t('shotList.unspecified')} · ${row.framing_focus || t('shotList.unspecified')}`],
          [t('shotList.productionField7'), row.camera_movement || t('shotList.productionStatic')],
          [t('shotList.productionField8'), `IN ${previous ? `from ${previous.shot_id}` : 'sequence start'} · ${required(seams[index].time_relation, 'time relation')} · ${stateLabel(required(shot.ledger_in, 'ledger IN'))} / OUT ${stateLabel(required(shot.ledger_out, 'ledger OUT'))} · owns ${changes.join('; ') || 'None'} / → ${shots[index + 1]?.shot_id || 'sequence end'} · ${row.transition_out || 'hard cut'} · ${required(outgoing.cut_point, 'cut point') || 'TBC'} · ${required(outgoing.motivation, 'motivation') || 'TBC'}`],
          [t('shotList.productionField9'), speech.join(' | ') || t('shotList.productionNone')],
          [t('shotList.productionField10'), `${row.sfx || t('shotList.productionNone')} · ${t('shotList.productionAudioRoute', { route: required(shot.audio_route, 'audio route') })}`],
        ]
        return <article key={row.id} className="rounded-xl border border-border bg-surface-1 p-4">
          <h4 className="mb-3 text-base font-medium text-agent-storyboard">{t('shotList.shotLabel', { number: index + 1 })} · {row.id}</h4>
          <dl className="space-y-2">{fields.map(([label, value]) => <div key={label} className="grid grid-cols-1 gap-1 border-b border-border-subtle pb-2 last:border-0 sm:grid-cols-[12rem_minmax(0,1fr)] sm:gap-3">
            <dt className="text-muted-foreground">{label}</dt><dd className="break-words">{value}</dd>
          </div>)}</dl>
        </article>
      })}
    </section>
    <section aria-label="Seam register" className="rounded-lg border border-border bg-surface-1 p-4">
      <h3 className="mb-2 font-medium">{t('shotList.productionSeams')}</h3>
      <ol className="space-y-1 text-muted-foreground">{seams.map(seam => <li key={required(seam.seam_id, 'seam ID')}>
        {t('shotList.productionSeamLine', { id: seam.seam_id, relation: seam.time_relation, carrier: seam.carrier || 'TBC', cut: seam.cut_point || 'TBC', execution: seam.execution || 'TBC' })}
      </li>)}</ol>
    </section>
    <section aria-label="Pending decisions" className="rounded-lg border border-border bg-surface-1 p-4">
      <h3 className="mb-2 font-medium">{t('shotList.productionPending')}</h3><p className="text-muted-foreground">{decisions.join('; ') || 'None'}</p>
      <p className="mt-2 text-muted-foreground">{t('shotList.productionLimitations')}</p>
    </section>
  </div>
}
